import { Link } from 'react-router-dom';
import {
  Truck, CalendarDays, FileText, Target, Users, Trophy,
  ArrowRight, Flame, Star, TrendingUp,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { ProgressRing } from '@/components/ui/ProgressRing';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useProfile } from '@/hooks/useProfile';
import { useGamification } from '@/hooks/useGamification';
import { useOpportunities } from '@/hooks/useOpportunities';
import { calculateProfileCompleteness, VEHICLE_CATEGORY_LABELS } from '@/types';
import type { VehicleCategory, Opportunity } from '@/types';

export function DashboardPage() {
  const { profile, loading: profileLoading } = useProfile();
  const { totalPoints, badges, loading: gamLoading } = useGamification();
  const { opportunities, loading: oppLoading } = useOpportunities();

  if (profileLoading) return <LoadingSpinner />;
  if (!profile) return null;

  const completeness = calculateProfileCompleteness(profile);
  const earnedBadges = badges.filter(b => b.earned);
  const recentOpportunities = opportunities.slice(0, 3);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome header */}
      <div>
        <h1 className="font-heading text-3xl tracking-wider">
          HOLA, {(profile.display_name || 'CONDUCTOR').toUpperCase()} 👋
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {profile.current_streak_days > 0 ? (
            <span className="flex items-center gap-1">
              <Flame size={14} className="text-brand-yellow" />
              Racha de {profile.current_streak_days} días activo
            </span>
          ) : (
            'Bienvenido a tu portal de conductor'
          )}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        {/* Profile Completeness */}
        <Card className="flex flex-col items-center py-4">
          <ProgressRing percentage={completeness.percentage} size={80} strokeWidth={6} label="Perfil" />
          {completeness.missing.length > 0 && (
            <Link to="/profile" className="text-xs text-brand-yellow font-medium mt-2 flex items-center gap-1">
              Completar <ArrowRight size={12} />
            </Link>
          )}
        </Card>

        {/* Points Widget */}
        <Card className="flex flex-col items-center justify-center py-4">
          <Star size={24} className="text-brand-yellow mb-1" />
          <p className="font-heading text-3xl tracking-wider">{totalPoints.toLocaleString()}</p>
          <p className="text-xs text-gray-500">Puntos</p>
          {earnedBadges.length > 0 && (
            <div className="flex gap-1 mt-2">
              {earnedBadges.slice(0, 3).map(b => (
                <Badge key={b.id} label={b.name} variant="yellow" size="sm" />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Quick action cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { to: '/vehicles', label: 'Vehículos', icon: Truck, color: 'bg-brand-yellow' },
          { to: '/availability', label: 'Calendario', icon: CalendarDays, color: 'bg-brand-black' },
          { to: '/documents', label: 'Documentos', icon: FileText, color: 'bg-brand-yellow' },
          { to: '/opportunities', label: 'Oportunidades', icon: Target, color: 'bg-brand-black' },
          { to: '/referrals', label: 'Referidos', icon: Users, color: 'bg-brand-yellow' },
          { to: '/gamification', label: 'Logros', icon: Trophy, color: 'bg-brand-black' },
        ].map(item => (
          <Link key={item.to} to={item.to}>
            <Card hover className="flex flex-col items-center py-4 gap-2">
              <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center`}>
                <item.icon size={20} className={item.color === 'bg-brand-black' ? 'text-brand-yellow' : 'text-brand-black'} />
              </div>
              <span className="text-xs font-medium text-center">{item.label}</span>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Opportunities */}
      {!oppLoading && recentOpportunities.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-xl tracking-wider">OPORTUNIDADES</h2>
            <Link to="/opportunities" className="text-xs text-brand-yellow font-medium flex items-center gap-1">
              Ver todas <ArrowRight size={12} />
            </Link>
          </div>
          <div className="space-y-2">
            {recentOpportunities.map((opp: Opportunity) => (
              <Link key={opp.id} to={`/opportunities`}>
                <Card hover className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target size={18} className="text-brand-yellow" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{opp.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">{VEHICLE_CATEGORY_LABELS[opp.vehicle_category as VehicleCategory] || opp.vehicle_category}</span>
                      {opp.zone_name && <span className="text-xs text-gray-400">• {opp.zone_name}</span>}
                    </div>
                  </div>
                  {opp.estimated_pay_range && (
                    <span className="text-xs font-semibold text-brand-black">{opp.estimated_pay_range}</span>
                  )}
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Activity feed placeholder */}
      <div>
        <h2 className="font-heading text-xl tracking-wider mb-3">ACTIVIDAD RECIENTE</h2>
        {!gamLoading && (
          <div className="space-y-2">
            {badges.filter(b => b.earned).length > 0 ? (
              badges.filter(b => b.earned).slice(0, 5).map(b => (
                <Card key={b.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Trophy size={16} className="text-brand-black" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Badge desbloqueado: {b.name}</p>
                    <p className="text-xs text-gray-500">{b.description}</p>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="text-center py-6">
                <TrendingUp size={24} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Completa acciones para ver tu actividad aquí</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
